import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { TProject } from '@/types/projects.ts';
import { uuidToColor } from '@/lib/utils/common.ts';

type TListViewMode = 'grid' | 'list';

export type TState = {
  projects: TProject[];
  projectsExist: boolean;
  projectsListView: TListViewMode;
  selectedProject: TProject | null;
  ideasExist: boolean;
  postsExist: boolean;
};

const initialState: TState = {
  projects: [],
  projectsExist: false,
  projectsListView: 'grid',
  selectedProject: null,
  ideasExist: false,
  postsExist: false,
};

type TActions = {
  setProjects: (projects: TProject[]) => void;
  setProjectsListView: (listViewMode: TListViewMode) => void;
  setSelectedProject: (project: TProject | null) => void;
  setIdeasExist: (ideasExist: boolean) => void;
  setPostsExist: (postsExist: boolean) => void;
};

type TStore = TState & TActions;

const useProjectsStore = create<TStore>()(
  persist(
    immer((set) => ({
      ...initialState,

      setProjects: (projects) => {
        set((state) => {
          state.projects = projects.map((c) => ({
            ...c,
            marker: uuidToColor(c.id),
          }));
          state.projectsExist = !!projects?.length;
        });
      },
      setSelectedProject: (project) => {
        set((state) => {
          state.postsExist = !!project?.has_posts;
          state.ideasExist = !!project?.has_ideas;

          state.selectedProject = project
            ? {
                ...project,
                marker: uuidToColor(project.id),
              }
            : project;
        });
      },
      setPostsExist: (postsExist) => {
        set((state) => ({ ...state, postsExist }));
      },
      setIdeasExist: (ideasExist) => set((state) => ({ ...state, ideasExist })),
      setProjectsListView: (listViewMode) => {
        set((state) => {
          state.projectsListView = listViewMode;
        });
      },
    })),
    {
      name: 'projects-storage',
      partialize: (state) => ({
        selectedProject: state.selectedProject,
        projectsListView: state.projectsListView,
      }),
    }
  )
);

export default useProjectsStore;
